/* ==========================================================================
   BRASSLUXE — artisan-hands.js
     - parallax artisan image (GSAP ScrollTrigger, desktop only)
     - count-up counters on scroll into view (rAF ease-out; reduced motion
       skips animation — final values are already server-rendered)
     - self-assembling zellige tile canvas (8-pointed stars, staggered,
       particleScale-scaled, dpr-aware, static when reduced motion)
   ========================================================================== */
(function () {
  'use strict';

  var BLX = window.BrassLuxe;
  if (!BLX) return;

  BLX.mount('.artisan-hands', function (section, blx) {
    var cleanups = [];
    var killed = false;

    /* ---------- image parallax (desktop, motion allowed) ---------- */
    var parallaxTrigger = null;
    var parallaxTween = null;
    if (!blx.reducedMotion && !blx.isMobile && blx.gsap && blx.ScrollTrigger) {
      var img = section.querySelector('.artisan-hands__img');
      var media = section.querySelector('[data-parallax-media]');
      if (img && media) {
        parallaxTween = blx.gsap.fromTo(img,
          { yPercent: -8 },
          {
            yPercent: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: media,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          });
        parallaxTrigger = parallaxTween.scrollTrigger;
      }
    }
    cleanups.push(function () {
      if (parallaxTrigger) parallaxTrigger.kill();
      if (parallaxTween) parallaxTween.kill();
    });

    /* ---------- counters ---------- */
    var counterEls = Array.prototype.slice.call(section.querySelectorAll('[data-counter]'));
    var counterRafs = [];
    var counterIO = null;

    function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

    function animateCounter(el) {
      var target = parseInt(String(el.getAttribute('data-target') || '').replace(/[^\d]/g, ''), 10);
      if (!target || isNaN(target)) return;
      var duration = 1400;
      var start = null;
      function tick(now) {
        if (killed) return;
        if (start === null) start = now;
        var p = Math.min((now - start) / duration, 1);
        el.textContent = String(Math.round(target * easeOut(p)));
        if (p < 1) counterRafs.push(requestAnimationFrame(tick));
        else el.textContent = String(target);
      }
      counterRafs.push(requestAnimationFrame(tick));
    }

    if (counterEls.length && !blx.reducedMotion && 'IntersectionObserver' in window) {
      counterIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            counterEls.forEach(animateCounter);
            counterIO.disconnect();
          }
        });
      }, { threshold: 0.4 });
      var countersList = section.querySelector('[data-counters]');
      if (countersList) counterIO.observe(countersList);
    }
    cleanups.push(function () {
      if (counterIO) counterIO.disconnect();
      counterRafs.forEach(cancelAnimationFrame);
      counterRafs.length = 0;
    });

    /* ---------- zellige canvas ---------- */
    var canvas = section.querySelector('[data-zellige-canvas]');
    var zelligeIO = null;
    var tileRaf = null;
    var tiles = [];

    if (canvas) {
      var ctx = canvas.getContext('2d');
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      function drawStar(cx, cy, r, alpha) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = '#C9A227';
        ctx.lineWidth = Math.max(1, dpr);
        var s = r * 1.42; /* square half-diagonal fit */
        for (var rot = 0; rot < 2; rot++) {
          ctx.save();
          ctx.rotate(rot * Math.PI / 4);
          ctx.strokeRect(-s / 2, -s / 2, s, s);
          ctx.restore();
        }
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      function buildTiles() {
        var rect = section.getBoundingClientRect();
        canvas.width = Math.max(1, Math.round(rect.width * dpr));
        canvas.height = Math.max(1, Math.round(rect.height * dpr));
        var cell = 96 * dpr;
        var cols = Math.ceil(canvas.width / cell);
        var rows = Math.ceil(canvas.height / cell);
        tiles = [];
        var scale = Math.max(blx.particleScale, 0.25);
        for (var y = 0; y < rows; y++) {
          for (var x = 0; x < cols; x++) {
            if (Math.random() > scale) continue;
            tiles.push({
              cx: (x + 0.5) * cell,
              cy: (y + 0.5) * cell,
              r: cell * 0.32,
              delay: Math.random() * 900,
              fromY: (Math.random() - 0.5) * 60 * dpr,
              fromScale: 0.3 + Math.random() * 0.3
            });
          }
        }
      }

      function renderStatic() {
        buildTiles();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        tiles.forEach(function (t) { drawStar(t.cx, t.cy, t.r, 1); });
      }

      function animateTiles() {
        var start = null;
        function tick(now) {
          if (killed) return;
          if (start === null) start = now;
          var elapsed = now - start;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          var done = true;
          tiles.forEach(function (t) {
            var p = Math.max(0, Math.min((elapsed - t.delay) / 700, 1));
            if (p < 1) done = false;
            if (p <= 0) return;
            var e = 1 - Math.pow(1 - p, 3);
            ctx.save();
            ctx.translate(t.cx, t.cy + t.fromY * (1 - e));
            var s = t.fromScale + (1 - t.fromScale) * e;
            ctx.scale(s, s);
            ctx.translate(-t.cx, -t.cy);
            drawStar(t.cx, t.cy, t.r, e);
            ctx.restore();
          });
          if (!done) tileRaf = requestAnimationFrame(tick);
          else tileRaf = null;
        }
        tileRaf = requestAnimationFrame(tick);
      }

      if (blx.reducedMotion) {
        renderStatic();
      } else if (blx.particleScale > 0 && 'IntersectionObserver' in window) {
        buildTiles();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        zelligeIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              zelligeIO.disconnect();
              animateTiles();
            }
          });
        }, { threshold: 0.2 });
        zelligeIO.observe(section);
      }
      /* particleScale 0 (density off) → canvas stays empty */

      cleanups.push(function () {
        if (zelligeIO) zelligeIO.disconnect();
        if (tileRaf) { cancelAnimationFrame(tileRaf); tileRaf = null; }
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        tiles.length = 0;
      });
    }

    return function () {
      killed = true;
      cleanups.forEach(function (fn) { fn(); });
    };
  });
})();
